'use client';

import { StateCreator } from 'zustand';
import cloneDeep from 'lodash-es/cloneDeep';
import { ROLE } from 'app/cart/constants';

export type CompletedAddressBook = {
    id: CartAddressBook['id'] | null;
    ship_from: CartAddressBook['ship_from'] | null;
    ship_to: CartAddressBook['ship_to'] | null;
    receiver: CartAddressBook['receiver'] | null;
    buyer: CartAddressBook['buyer'] | null;
    subdivision: Subdivision | null;
};

interface CartShippingState {
    shippingProfileId: string | null;
    latestUpdateField: [ROLE, keyof CartAddressDeliveryBase | null];
    editingAddress: boolean;
    shippingMethod: Array<CartShippingMethod> | null;
    shippingType: ShippingType | null;
    shipTo: string | null;
    shipFrom: string | null;
    addressCountry: { label: string; value: string }[];
    addressForm: {
        receiver: CartAddressForm;
        buyer: CartAddressForm;
    };
    addressFormFieldIndexMap: {
        receiver?: {
            [key in keyof CartAddressDeliveryBase]: number;
        };
        buyer?: {
            [key in keyof CartAddressDeliveryBase]: number;
        };
    };
    draftAddressBook: CompletedAddressBook;
    completedAddressBook: CompletedAddressBook;
    isReceiverAsBuyer: boolean;
}

interface CartShippingActions {
    updateEditingAddress: (editing: boolean) => void;
    updateShippingMethod: (shippingMethod: Array<CartShippingMethod>) => void;
    updateShippingType: (shippingType: ShippingType | null) => void;
    updateShipTo: (shipTo: string | null) => void;
    updateShipFrom: (shipFrom: string | null) => void;
    updateAddressBook: (addressBook: CartAddressBook) => void;
    updateShippingProfileId: (shippingProfileId: string | null) => void;
    getAddressFormFieldValue: (
        role: ROLE,
        fieldName: keyof CartAddressDeliveryBase
    ) => string | null;
    updateAddressFormFieldValue: (
        role: ROLE,
        fieldName: keyof CartAddressDeliveryBase,
        value: string
    ) => void;
    partialUpdateAddressForm: (role: ROLE, fields: AddressAutocomplete) => void;
    updateAddressForm: (role: ROLE, addressForm: CartAddressForm) => void;
    updateIsReceiverAsBuyer: (isReceiverAsBuyer: boolean) => void;
    updateAddressCountry: (addressCountry: CartShippingState['addressCountry']) => void;
    updateLatestUpdateField: (
        latestUpdateField: [ROLE, keyof CartAddressDeliveryBase | null]
    ) => void;
    syncCompletedToDraft: () => void;
    __testOnlyResetShippingState: () => void;
}

export type CartShippingSlice = CartShippingState & CartShippingActions;

const defaultShippingState: CartShippingState = {
    shippingProfileId: null,
    latestUpdateField: [ROLE.RECEIVER, null],
    editingAddress: false,
    addressCountry: [],
    shippingMethod: null,
    shippingType: null,
    shipTo: null,
    shipFrom: null,
    addressFormFieldIndexMap: {},
    draftAddressBook: {
        id: null,
        ship_from: null,
        ship_to: null,
        receiver: null,
        buyer: null,
        subdivision: null,
    },
    completedAddressBook: {
        id: null,
        ship_from: null,
        ship_to: null,
        receiver: null,
        buyer: null,
        subdivision: null,
    },
    addressForm: {
        receiver: [] as unknown as CartAddressForm,
        buyer: [] as unknown as CartAddressForm,
    },
    isReceiverAsBuyer: true,
};

export const createCartShippingSlice: StateCreator<
    CartShippingSlice,
    [],
    [],
    CartShippingSlice
> = (set, get) => ({
    ...defaultShippingState,
    updateEditingAddress: (editing) => {
        set({ editingAddress: editing });
    },
    partialUpdateAddressForm: (role, fields) => {
        const { addressForm, draftAddressBook } = get();

        if (!addressForm) {
            return;
        }

        const newAddressForm = addressForm[role];

        const newDraftAddressBookByRole = draftAddressBook[role];

        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];

            for (let j = 0; j < newAddressForm.length; j++) {
                const newAddressFormField = newAddressForm[j];

                if (newAddressFormField?.key === field?.key) {
                    newAddressForm[j] = field;

                    if (newDraftAddressBookByRole?.[field.key]) {
                        newDraftAddressBookByRole[field.key] = field.field.value || '';
                    }
                }
            }
        }

        set({
            draftAddressBook: {
                ...draftAddressBook,
                [role]: {
                    ...draftAddressBook[role],
                    ...newDraftAddressBookByRole,
                },
            },
            addressForm: {
                ...addressForm,
                [role]: newAddressForm,
            },
        });
    },

    updateAddressForm: (role, newAddressForm) => {
        const { addressForm, addressFormFieldIndexMap } = get();

        const newAddressFormFieldIndexMap = newAddressForm.reduce((acc, field, idx) => {
            return {
                ...acc,
                [field.key]: idx,
            };
        }, {});

        set({
            addressFormFieldIndexMap: {
                ...addressFormFieldIndexMap,
                [role]: newAddressFormFieldIndexMap,
            },
            addressForm: {
                ...addressForm,
                [role]: newAddressForm,
            },
        });
    },

    getAddressFormFieldValue: (role, fieldName) => {
        const { addressForm, addressFormFieldIndexMap, draftAddressBook } = get();

        const index = addressFormFieldIndexMap[role]?.[fieldName];

        if (!addressForm || typeof index === 'undefined' || !addressForm[role][index]) {
            return null;
        }

        const { field } = addressForm[role][index];

        return field.value || draftAddressBook[role]?.[fieldName] || null;
    },

    updateAddressFormFieldValue: (role, fieldName, value) => {
        const { addressForm, addressFormFieldIndexMap } = get();

        const index = addressFormFieldIndexMap[role]?.[fieldName];

        if (!addressForm || typeof index === 'undefined') {
            return;
        }

        const newAddressForm = cloneDeep(addressForm);

        const { field } = newAddressForm[role][index];

        // side effect operation
        field.value = value;

        set({
            addressForm: newAddressForm,
            latestUpdateField: [role, fieldName],
        });
    },

    updateAddressBook: (addressBook) => {
        set({
            draftAddressBook: addressBook,
            completedAddressBook: addressBook,
        });
    },
    updateShippingMethod: (shippingMethod) => {
        set({ shippingMethod });
    },
    updateShippingType: (shippingType) => {
        set({ shippingType: shippingType || null });
    },
    updateShipTo: (shipTo) => {
        set({ shipTo: shipTo || null });
    },
    updateShipFrom: (shipFrom) => {
        set({ shipFrom: shipFrom || null });
    },
    updateIsReceiverAsBuyer: (isReceiverAsBuyer) => {
        set({ isReceiverAsBuyer });
    },
    updateAddressCountry: (addressCountry) => {
        set({ addressCountry });
    },
    syncCompletedToDraft: () => {
        const { completedAddressBook } = get();

        set({ draftAddressBook: cloneDeep(completedAddressBook) });
    },
    updateLatestUpdateField: (latestUpdateField) => {
        set({ latestUpdateField });
    },
    updateShippingProfileId: (shippingProfileId) => {
        set({ shippingProfileId });
    },
    __testOnlyResetShippingState: () => {
        set({
            ...defaultShippingState,
        });
    },
});
